// Persistence adapter: the assistant's message store.
// See .claude/context/persistence.md for why this seam exists.
import { useMemo } from 'react';
import { useIndexedDBStore } from './idb/useIndexedDBStore';

export const MESSAGE_STORE_NAME = 'messages';

/**
 * Store-bound message-store methods, with `getAll` scoped to one session. The filter matches the
 * delete rule in `cleanOrphans`. See .claude/context/persistence.md
 *
 * @param {string} [sessionId] - the holder's session id; rows from any other session are hidden.
 *   If it is absent, `getAll` returns every row: a blank history is worse than a stale one.
 * @returns {object} store-bound, promise-returning message-store methods
 *   ({ getAll, add, update, get, put, insertIfAbsent, clearMessages, cleanOrphans }).
 */
export const useMessageStore = (sessionId) => {
  const store = useIndexedDBStore(MESSAGE_STORE_NAME);

  // Memoized so `getAll` keeps a stable identity: it is handed out as
  // `iDBGetAll_messages` and sits in the orchestrator's useCallback deps.
  return useMemo(() => ({
    ...store,
    getAll: () => store.getAll().then((rows) => (
      sessionId ? rows.filter((row) => row.sessionId === sessionId) : rows
    )),
  }), [store, sessionId]);
};
